/*
 * Quantity break cards.
 *
 * Each card carries a unit count and the price shown on that card. Selecting
 * one:
 *   1. writes the count into the product's quantity input and fires a change
 *      event, so Dawn's QuantityInput, the volume-pricing note and the cart
 *      quantity rules all react as they would to a manual edit of the field;
 *   2. rewrites the price in the main price container and in the add-to-cart
 *      label so both agree with the selected card.
 *
 * The radios deliberately have no `form` attribute: they are UI only and must
 * not be posted with the add-to-cart form.
 */
if (!customElements.get('quantity-breaks')) {
  customElements.define(
    'quantity-breaks',
    class QuantityBreaks extends HTMLElement {
      connectedCallback() {
        this.quantityInput = document.getElementById(this.dataset.quantityInput);
        if (!this.quantityInput) return;

        this.onBreakChange = this.onBreakChange.bind(this);
        this.onQuantityChange = this.onQuantityChange.bind(this);

        // Captured before anything is rewritten, so a quantity that matches no
        // tier can put the theme's own prices back.
        this.defaultPrices = new Map();
        this.priceTargets.forEach((element) => this.defaultPrices.set(element, element.textContent));

        this.addEventListener('change', this.onBreakChange);
        // Keep the cards honest when the stepper is used directly.
        this.quantityInput.addEventListener('change', this.onQuantityChange);

        this.syncFromQuantity();
      }

      disconnectedCallback() {
        this.quantityInput?.removeEventListener('change', this.onQuantityChange);
      }

      get radios() {
        return Array.from(this.querySelectorAll('input[type="radio"]'));
      }

      // Every element whose text should track the selected pack.
      get priceTargets() {
        const targets = [];

        const priceContainer = document.getElementById(this.dataset.priceContainer);
        if (priceContainer) {
          // price.liquid also puts .price-item--regular on the struck-through
          // compare-at price. That one is a <s> and must keep its own figure.
          targets.push(
            ...Array.from(priceContainer.querySelectorAll('.price-item--regular, .price-item--sale')).filter(
              (element) => element.tagName !== 'S'
            )
          );
        }

        const submitButton = document.getElementById(this.dataset.submitButton);
        const submitPrice = submitButton?.querySelector('.product-form__submit-price');
        if (submitPrice) targets.push(submitPrice);

        return targets;
      }

      onBreakChange(event) {
        const radio = event.target.closest('input[type="radio"]');
        if (!radio) return;

        this.quantityInput.value = radio.value;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.applyPrice(radio);
      }

      onQuantityChange() {
        this.syncFromQuantity();
      }

      // Check the card matching the current quantity, or clear them all when the
      // quantity does not correspond to any tier.
      //
      // Everything downstream of a quantity change runs from here: picking a card
      // writes the input and dispatches change, which lands back in this method,
      // so both the cards and the stepper take the same path.
      syncFromQuantity() {
        const current = parseInt(this.quantityInput.value, 10) || 0;

        // Highest tier at or below the current quantity, not an exact match: 3
        // stays on the 2-pack and 5 or more stays on the 4-pack, rather than
        // leaving no card active once the quantity falls between tiers.
        let selected = null;
        this.radios.forEach((radio) => {
          const tier = parseInt(radio.value, 10);
          if (tier <= current && (selected === null || tier > parseInt(selected.value, 10))) {
            selected = radio;
          }
        });

        this.radios.forEach((radio) => {
          radio.checked = radio === selected;
        });

        this.applyPrice(selected);
        this.updateShipMeter(current);
      }

      updateShipMeter(quantity) {
        const meter = this.querySelector('[data-ship-meter]');
        if (!meter) return;

        const threshold = parseInt(meter.dataset.shipQty, 10);
        if (!threshold) return;

        const remaining = Math.max(threshold - quantity, 0);
        const percent = Math.min(Math.round((quantity / threshold) * 100), 100);

        const bar = meter.querySelector('[data-ship-bar]');
        const track = meter.querySelector('[data-ship-track]');
        if (bar) bar.style.width = `${percent}%`;
        if (track) track.setAttribute('aria-valuenow', percent);

        const pending = meter.querySelector('[data-ship-pending]');
        const reached = meter.querySelector('[data-ship-reached]');
        pending?.toggleAttribute('hidden', remaining === 0);
        reached?.toggleAttribute('hidden', remaining > 0);

        if (remaining === 0) return;

        const remainingText = meter.querySelector('[data-ship-remaining]');
        if (remainingText) remainingText.textContent = remaining;
        meter.querySelector('[data-ship-plural]')?.toggleAttribute('hidden', remaining === 1);
      }

      // Live total for the quantity actually in the field: unit x quantity, less
      // the discount of whichever tier is active. At a quantity that lands on a
      // tier this reproduces the figure on that card; in between, it shows the
      // real total rather than the nearest card's price.
      applyPrice(radio) {
        const unit = parseInt(this.dataset.unitPrice, 10);
        const quantity = parseInt(this.quantityInput.value, 10) || 0;

        this.priceTargets.forEach((element) => {
          if (!this.defaultPrices.has(element)) this.defaultPrices.set(element, element.textContent);
        });

        if (!unit || quantity < 1) {
          this.priceTargets.forEach((element) => {
            element.textContent = this.defaultPrices.get(element);
          });
          return;
        }

        const discount = parseInt(radio?.dataset.discount, 10) || 0;
        const total = Math.max(unit * quantity - discount, 0);
        const text = this.formatMoney(total);

        this.priceTargets.forEach((element) => {
          element.textContent = text;
        });
      }

      // Minimal port of Shopify's money formatter, covering the four
      // placeholders a money_format can use.
      formatMoney(cents) {
        const format = this.dataset.moneyFormat || '${{amount}}';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return format;

        const value = cents / 100;
        let amount;

        switch (match[1]) {
          case 'amount_no_decimals':
            amount = this.groupDigits(value, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            amount = this.groupDigits(value, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            amount = this.groupDigits(value, 0, '.', ',');
            break;
          default:
            amount = this.groupDigits(value, 2, ',', '.');
        }

        return format.replace(placeholder, amount);
      }

      groupDigits(value, decimals, thousandsSeparator, decimalSeparator) {
        const parts = value.toFixed(decimals).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
        return parts.join(decimalSeparator);
      }
    }
  );
}
